/**
 * Registri di numerazione (CF08/10) — CL05.
 *
 * Qui stanno le regole che si verificano senza un browser. Il resto — cosa
 * mostra il pannello — sta nei componenti.
 *
 * Tre decisioni, e ognuna ha un costo se sbagliata:
 *
 * - **`AZZERA` si confronta letteralmente.** Il contratto rifiuta qualunque
 *   grafia diversa: nessun `trim`, nessun `toLowerCase`. Accettare `" azzera "`
 *   significherebbe che due implementazioni diverse (qui e sul server)
 *   decidono cose diverse sullo stesso testo — e una delle due cancellerebbe
 *   una numerazione che l'altra avrebbe protetto.
 * - **Il reset annuale vuole un anno nel pattern.** Senza segmento anno non c'e'
 *   niente da azzerare: un interruttore che sembra acceso e non fa niente e'
 *   peggio di un interruttore assente.
 * - **L'anteprima non e' una promessa.** Il contratto la marca `advisory` e non
 *   riserva nessun numero: un codice mostrato come "il prossimo" sarebbe falso
 *   al secondo utente che avvia insieme al primo.
 */

import type { RegistryConfiguration, RegistryPreview, SequencePattern } from "model/CustomFields";

/** La parola che abilita il reset. Non si traduce e non si normalizza. */
export const RESET_KEYWORD = "AZZERA";

/** I limiti del contratto sul pattern. */
export const PATTERN_LIMITS = {
  prefix: 64,
  postfix: 64,
  padding: { min: 1, max: 8 },
  code: 120,
  reason: 1000,
} as const;

/**
 * La parola corrisponde?
 *
 * Confronto **letterale**: `" azzera "` non corrisponde, `"Azzera"` nemmeno.
 * E' il server a decidere, e questa funzione deve dire la stessa cosa — non una
 * versione piu' permissiva che lascerebbe credere abilitato cio' che il server
 * rifiuta.
 */
export const keywordMatches = (value: string): boolean => value === RESET_KEYWORD;

export interface ResetDraft {
  /** Il motivo dell'operazione, come lo scrive l'utente. */
  reason: string;
}

/**
 * Il reset si puo' inviare?
 *
 * La parola di conferma la fornisce il client (`RESET_KEYWORD`), quindi non e'
 * una cosa da verificare qui: l'unica cosa che resta e' il motivo scritto, che
 * il server pretende (`_require_reason`) e senza il quale il reset torna 422.
 */
export function canSubmitReset(draft: ResetDraft): {
  ok: boolean;
  missing: string[];
} {
  const missing: string[] = [];
  // Solo spazi non sono un motivo: il server vuole un testo, non un vuoto.
  if (draft.reason.trim() === "") missing.push("custom_fields.sequence.reset.reason");
  return { ok: missing.length === 0, missing };
}

/**
 * Il prossimo numero dopo l'ultimo emesso.
 *
 * L'override mostra "ultimo N -> successivo N+1": far vedere solo il campo da
 * compilare lascerebbe il progettista a calcolare a mente cosa comporta.
 */
export const nextAfter = (lastIssued: number | null | undefined): number | null =>
  lastIssued === null || lastIssued === undefined ? null : lastIssued + 1;

/**
 * Il numero a cui sta **adesso** il contatore.
 *
 * E' il rovescio di `nextAfter`: il server manda `nextValue`, cioe' il prossimo
 * codice che emettera', e il valore corrente è il prossimo meno uno. Non si usa
 * `lastIssued` per questo: quello e' l'ultimo codice **emesso**, e un
 * azzeramento o un override non lo toccano — mostrerebbe un numero che il
 * contatore non ha piu'. Il cursore invece torna all'iniziale meno uno, cioe' a
 * zero con l'iniziale di partenza.
 *
 * `nextValue` arriva come decimale canonico (stringa): vuota o assente
 * significa "non lo so", e li' si mostra un trattino invece di inventare uno
 * zero.
 */
export const currentValue = (nextValue: string | null | undefined): string | null => {
  if (nextValue === null || nextValue === undefined || nextValue.trim() === "") return null;
  const prossimo = Number(nextValue);
  return Number.isFinite(prossimo) ? String(prossimo - 1) : null;
};

/**
 * Il reset annuale e' coerente col pattern?
 *
 * Restituisce la chiave del problema, o `null`. Il contratto rifiuta la
 * combinazione; il client la mostra **prima** dell'invio, accanto al
 * controllo che la causa.
 */
export function annualResetIssue(config: {
  annualReset: boolean;
  pattern: SequencePattern;
}): string | null {
  if (!config.annualReset) return null;
  if (config.pattern.year === "none") {
    return "custom_fields.sequence.error.annual_reset_needs_year";
  }
  return null;
}

/** I problemi del pattern, come chiavi di traduzione. */
export function patternIssues(pattern: SequencePattern): string[] {
  const issues: string[] = [];

  if (pattern.prefix.length > PATTERN_LIMITS.prefix) {
    issues.push("custom_fields.error.validation_failed");
  }
  if (pattern.postfix.length > PATTERN_LIMITS.postfix) {
    issues.push("custom_fields.error.validation_failed");
  }
  const { min, max } = PATTERN_LIMITS.padding;
  if (pattern.padding < min || pattern.padding > max) {
    issues.push("custom_fields.error.invalid_range");
  }

  return issues;
}

/** I problemi di una configurazione completa. */
export function registryConfigIssues(config: RegistryConfiguration): string[] {
  const issues = patternIssues(config.pattern);

  // Il cursore parte uno sotto il primo codice emesso: partire da zero lo
  // metterebbe a -1.
  if (!Number.isInteger(config.initial) || config.initial < 1) {
    issues.push("custom_fields.error.invalid_range");
  }

  const annual = annualResetIssue(config);
  if (annual) issues.push(annual);

  return issues;
}

/**
 * L'anteprima si puo' mostrare come indicativa?
 *
 * Sempre, ma la funzione esiste per impedire l'uso opposto: un chiamante che
 * volesse trattarla come vincolante trova qui il posto dove non si puo' fare.
 * Il contratto la marca `advisory`, e il client non ha modo di renderla una
 * garanzia.
 */
export const previewIsAdvisory = (preview: RegistryPreview | null): boolean =>
  preview?.advisory === true;
